import { useParticipant } from '@videosdk.live/react-sdk'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMeetingAppContext } from '../MeetingAppContextDef'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { CornerDisplayName } from '@/components/CornerDisplayName'
import { useUser } from '@clerk/clerk-react'
import { useTurns } from '@/contexts/TurnsProvider'
import { SpeakingRole } from '@db8/shared'
import { useContainerDimensionsById } from './ParticipantGrid'
import { useAzureTts } from '@/contexts/AzureTtsProvider'
import ProfileImage from './ProfileImage'

export function ParticipantView({
  participantId,
  type,
  participantLength,
  containerHeight,
  containerWidth,
  index,
}) {
  const { hallUsers, isModerator, getUser, moderatorUsers, isBot } = useHallUsers()
  const { user: currentUser } = useUser()
  const { pinnedParticipants } = useTurns()
  const hallUser = getUser(participantId)
  const { isPlaying } = useAzureTts()

  const [animationPhase, setAnimationPhase] = useState<'idle' | 'pulsing' | 'exiting'>('idle')
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const participant = useParticipant(hallUser?.videoSDKId)
  const globalUser = hallUsers?.find((u) => participantId === u.id)
  const isBotUser = isBot(globalUser)

  // Decide the "user" shape once
  const user = isBotUser
    ? {
        displayName: globalUser?.displayName,
        webcamStream: null as any,
        micStream: null as any,
        webcamOn: false,
        micOn: false,
        isLocal: false,
        mode: 'CONFERENCE',
        isActiveSpeaker: false,
      }
    : participant

  // Safe derived values (no early return before hooks)
  const webcamOn = !!user?.webcamOn
  const micOn = !!user?.micOn
  const isLocal = !!user?.isLocal
  const isActiveSpeaker = !!user?.isActiveSpeaker
  const displayName = user?.displayName ?? hallUser?.displayName ?? 'Participant'

  const { selectedSpeaker } = useMeetingAppContext()

  // Media elements & persistent MediaStreams
  const micRef = useRef<HTMLAudioElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const audioStreamRef = useRef<MediaStream | null>(null)
  const videoStreamRef = useRef<MediaStream | null>(null)

  const [mouseOver, setMouseOver] = useState(false)
  const { containerWidth: currentContainerWidth } = useContainerDimensionsById(
    `user-${hallUser?.id}`,
  )
  const participantRef = useRef<HTMLDivElement | null>(null)

  const safePlay = async (mediaElement: HTMLMediaElement) => {
    try {
      const playPromise = mediaElement.play()
      if (playPromise && typeof (playPromise as Promise<void>).then === 'function') {
        await playPromise
      }
    } catch (err: any) {
      const message = String(err?.message || err || '').toLowerCase()

      // Ignore common, non-fatal cases:
      const isAutoplayBlocked = message.includes('notallowederror') // autoplay policy
      const interruptedByLoad = message.includes('interrupted by a new load request')
      const interruptedByPause = message.includes('interrupted by a call to pause()')
      if (isAutoplayBlocked || interruptedByLoad || interruptedByPause) {
        return // swallow
      }

      // no-op
    }
  }

  // Set speaker device (where supported)
  useEffect(() => {
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox')
    const audioElement = micRef.current
    if (!audioElement || !selectedSpeaker?.id) return
    try {
      if (!isFirefox && 'setSinkId' in (audioElement as any)) {
        ;(audioElement as any).setSinkId(selectedSpeaker.id)
      }
    } catch (err) {
      // no-op
    }
  }, [selectedSpeaker])

  // AUDIO wiring
  useEffect(() => {
    const audioElement = micRef.current
    if (!audioElement) return

    if (user?.micOn && user?.micStream?.track) {
      if (!audioStreamRef.current) audioStreamRef.current = new MediaStream()
      const audioStream = audioStreamRef.current

      const [oldTrack] = audioStream.getAudioTracks()
      if (oldTrack) audioStream.removeTrack(oldTrack)
      audioStream.addTrack(user.micStream.track)

      if (audioElement.srcObject !== audioStream) audioElement.srcObject = audioStream

      const timer = setTimeout(() => {
        // avoid double-play if already running
        if (audioElement.paused) safePlay(audioElement)
      }, 0)

      return () => clearTimeout(timer)
    } else {
      if (audioStreamRef.current) {
        audioStreamRef.current
          .getAudioTracks()
          .forEach((t) => audioStreamRef.current!.removeTrack(t))
        audioStreamRef.current = null
      }
      audioElement.srcObject = null
    }
  }, [user?.micOn, user?.micStream])

  // VIDEO wiring
  useEffect(() => {
    const videoElement = videoRef.current
    if (!videoElement) return

    if (user?.webcamOn && user?.webcamStream?.track) {
      if (!videoStreamRef.current) videoStreamRef.current = new MediaStream()
      const videoStream = videoStreamRef.current

      const [oldTrack] = videoStream.getVideoTracks()
      if (oldTrack) videoStream.removeTrack(oldTrack)
      videoStream.addTrack(user.webcamStream.track)

      if (videoElement.srcObject !== videoStream) videoElement.srcObject = videoStream

      const onLoaded = () => {
        if (videoElement.paused) safePlay(videoElement)
      }
      videoElement.addEventListener('loadedmetadata', onLoaded, { once: true })

      return () => {
        videoElement.removeEventListener('loadedmetadata', onLoaded)
      }
    } else {
      if (videoStreamRef.current) {
        videoStreamRef.current
          .getVideoTracks()
          .forEach((t) => videoStreamRef.current!.removeTrack(t))
        videoStreamRef.current = null
      }
      if (videoElement.srcObject) videoElement.srcObject = null
    }
  }, [user?.webcamOn, user?.webcamStream])

  // Speaker pulse animation
  const prevIsActive = useRef(isActiveSpeaker)
  useEffect(() => {
    // For bots (no SDK active speaker), pulse ONLY when this bot is the current/pinned speaker and hall is playing
    const isPinnedSpeaker = hallUser?.id ? pinnedParticipants.includes(hallUser.id) : false
    const shouldPulseBot = Boolean(isBotUser && isPlaying && isPinnedSpeaker)

    if (shouldPulseBot) {
      setAnimationPhase('pulsing')
      return
    }

    if (isBotUser && !shouldPulseBot && animationPhase === 'pulsing') {
      setAnimationPhase('exiting')
      return
    }

    // For humans, follow VideoSDK active speaker flag
    if (isActiveSpeaker !== prevIsActive.current) {
      prevIsActive.current = isActiveSpeaker

      if (isActiveSpeaker) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        setAnimationPhase('pulsing')
      } else {
        timeoutRef.current = setTimeout(() => {
          setAnimationPhase('exiting')
          timeoutRef.current = null
        }, 3000)
      }
    }
  }, [isActiveSpeaker, isPlaying, isBotUser, animationPhase, pinnedParticipants, hallUser?.id])

  useEffect(() => {
    if (animationPhase === 'exiting') {
      const cleanup = setTimeout(() => setAnimationPhase('idle'), 500)
      return () => clearTimeout(cleanup)
    }
  }, [animationPhase])

  // Retry autoplay on first user gesture
  useEffect(() => {
    const audioElement = micRef.current
    const videoElement = videoRef.current
    if (!audioElement && !videoElement) return

    let tried = false
    const onFirstGesture = () => {
      if (tried) return
      tried = true
      if (audioElement?.srcObject && audioElement.paused) safePlay(audioElement)
      if (videoElement?.srcObject && videoElement.paused) safePlay(videoElement)
      document.removeEventListener('pointerdown', onFirstGesture)
      document.removeEventListener('keydown', onFirstGesture)
    }

    document.addEventListener('pointerdown', onFirstGesture, { once: true })
    document.addEventListener('keydown', onFirstGesture, { once: true })
    return () => {
      document.removeEventListener('pointerdown', onFirstGesture)
      document.removeEventListener('keydown', onFirstGesture)
    }
  }, [])

  const getBgColor = useMemo(() => {
    if (hallUser?.speakingRole === SpeakingRole.Affirmative) return 'border-green-500'
    if (hallUser?.speakingRole === SpeakingRole.Negative) return 'border-blue-500'
    if (hallUser?.speakingRole === SpeakingRole.Moderator) return 'border-purple-500'
    return 'border-gray-600 dark:border-white'
  }, [hallUser?.speakingRole])

  const getBlobClass = useMemo(() => {
    if (animationPhase === 'idle') return ''

    const baseClass = (() => {
      switch (hallUser?.speakingRole) {
        case SpeakingRole.Affirmative:
          return 'pulse-ring-affirmative'
        case SpeakingRole.Negative:
          return 'pulse-ring-negative'
        case SpeakingRole.Moderator:
          return 'pulse-ring-moderator'
        default:
          return ''
      }
    })()

    if (animationPhase === 'exiting') return `${baseClass} pulse-ring-exit`
    return baseClass
  }, [animationPhase, hallUser?.speakingRole])

  const desiredMaxHeight = useMemo(() => {
    if (type === 'pinned') return containerHeight
    if (type === 'moderators') return containerHeight
    if (participantLength === 1) return containerHeight - 50
    return currentContainerWidth - 50
  }, [type, participantLength, containerHeight, currentContainerWidth])

  const maxHeight = Math.min(desiredMaxHeight, currentContainerWidth)

  const pinnedHeight = useMemo(() => {
    if (moderatorUsers.length > 0 && type === 'pinned') return 'max-h-[70vh]'
    if (type === 'pinned') return 'h-full'
    return ''
  }, [moderatorUsers.length, type])

  return (
    <div className="w-full h-full flex justify-center" style={{ height: '100%' }}>
      <div
        ref={participantRef}
        id={hallUser ? `user-${hallUser.id}` : undefined}
        onMouseEnter={() => setMouseOver(true)}
        onMouseLeave={() => setMouseOver(false)}
        className={`relative overflow-hidden rounded-lg border-2 bg-white dark:bg-gray-900 w-full h-full ${pinnedHeight} ${getBgColor}`}
        style={{ height: '100%', width: '100%' }}
      >
        {/* Remote Audio */}
        <audio ref={micRef} autoPlay muted={isLocal} />

        {/* Video */}
        {webcamOn ? (
          <div className="absolute inset-0 overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              controls={false}
              className={`h-full w-full object-cover object-center ${
                isLocal ? 'scale-x-[-1]' : ''
              }`}
              onError={() => {}}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full w-full relative">
            <div
              className={`
                absolute z-0 
                w-full h-full
                ${type === 'moderators' ? 'max-w-[50%] max-h-[50%]' : 'max-w-[70%] max-h-[70%]'} 
                ${getBlobClass}
              `}
            />
            <ProfileImage
              user={hallUser}
              className={`${
                type === 'moderators' ? 'max-w-[100%] max-h-[100%]' : 'max-w-[70%] max-h-[70%]'
              } object-cover rounded-full absolute text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl  border-2 ${getBgColor}`}
            />
          </div>
        )}

        <CornerDisplayName
          {...{
            isLocal,
            displayName,
            micOn,
            webcamOn,
            isPresenting: false,
            participantId,
            mouseOver,
            isActiveSpeaker,
            isModerator,
            isUser: hallUser?.clerkId === currentUser?.id,
          }}
        />
      </div>
    </div>
  )
}
